import React, { useCallback } from 'react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SetupLayout } from '../../components/layout/SetupLayout';
import { logger } from '../../utils/logger';
import { useAuth } from '../../hooks/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFetchResources, useFetchOrganizations } from '../../hooks/api';
import { useResourceFilters, useResourceSelection } from '../../hooks/resource';
import { useModalState, useOutsideClick } from '../../hooks/ui';
import { Modal } from '../../components/Modals/Shared/Modal';
import OrganizationSelector from '../../components/organization/OrganizationSelector';
import ResourceSelector from '../../components/resource/ResourceSelector';
import SelectedResourcesTableHeader from '../../components/resource/table/SelectedResourcesTableHeader';
import SelectedResourcesTableBody from '../../components/resource/table/SelectedResourcesTableBody';
import SelectedResourcesTableButtons from '../../components/resource/table/SelectedResourcesTableButtons';
import createMigration from '../../services/Migration/createMigrationService';
import { mapResourceTypeToApiPath, mapApiTypeToDisplayName } from '../../utils/resourceTypeMapper';
import SchedulePicker from '../../components/ScheduleSnapshot/SchedulePicker';
import type { Schedule } from '../../components/ScheduleSnapshot/SchedulePicker';
import ScheduleToCronExpression from '../../components/ScheduleSnapshot/ScheduleToCronExpression';
import { Tooltip } from '../../components/Tooltip';
import { RESOURCE_TYPES } from '../../utils/resourceTypes';
import { useResourceSearch } from '../../hooks/ui';
import { clearOrgCache } from '../../utils/resourceCache';
import { getSchedule, getScheduleDisplay } from '../../utils/cron';
import { getLocalTimezoneAbbr } from '../../utils/formatters';
import { formatRegion } from '../../utils/formatters';
import updateMigration from '../../services/Migration/updateMigrationService';
import { MigrationStatusEnum as MigrationStatus } from '../../types';
import { ui } from '../../config/uiSizes';

const CaptureSnapshot: React.FC = () => {
  const navigate = useNavigate();
  const selectedResourceTitle = 'Selected Resources Included in Snapshot';
  const [snapshotName, setSnapshotName] = React.useState('');
  const { userInfo, accessToken } = useAuth();
  const [sourceOrganization, setSourceOrganization] = React.useState('');
  const { organizations, fetchOrganizations } = useFetchOrganizations();
  const [resourceType, setResourceType] = React.useState('');
  const [selectedResourceTypes, setSelectedResourceTypes] = React.useState<string[]>([]);
  const [resourceSearch, setResourceSearch] = React.useState('');
  const [resourcesDropdownOpen, setResourcesDropdownOpen] = React.useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);
  const [confirmScope, setConfirmScope] = React.useState<'all' | 'current'>('all');
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = React.useState<string[]>([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = React.useState(false);
  const [isFullSnapshotMode, setIsFullSnapshotMode] = React.useState(false);
  const [isPrePopulatedForm, setIsPrePopulatedForm] = React.useState(false);
  const [prePopulatedFormId, setPrePopulatedFormId] = React.useState('');
  const { modalState, handleCaptureSnapshotClick, handleModalClose } = useModalState();
  const dropdownRef = React.useRef<HTMLDivElement | null>(null);
  const dropdownButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const [confirmLoading, setConfirmLoading] = React.useState(false);
  const [isRecurringEnabled, setIsRecurringEnabled] = React.useState(false);
  const [typeSearch, setTypeSearch] = React.useState('');
  const [nameSearch, setNameSearch] = React.useState('');
  const [includeDependencies, setIncludeDependencies] = React.useState(true);
  const location = useLocation();
  const [snapshotSchedule, setSnapshotSchedule] = React.useState<Schedule>({
    days: [],
    hour: 7,
    minute: 0,
    ampm: 'AM'
  });
  const snapshotCronExpression = ScheduleToCronExpression({
    ...snapshotSchedule
  });
  const snapshotScheduleDisplay = getScheduleDisplay(snapshotCronExpression);

  const getOrganizationKey = useCallback((org: { name: string; shortName?: string }): string => {
    const shortName = org.shortName?.trim();
    return shortName && shortName.length > 0 ? shortName : org.name;
  }, []);

  const findOrganization = useCallback(
    (value: string) => organizations.find((org) => getOrganizationKey(org) === value || org.name === value),
    [organizations, getOrganizationKey]
  );

  const {
    resources,
    loading: loadingResources,
    error: resourceFetchError,
    fetchProgress,
    fetchMultipleResourceTypes,
    clearResources,
    typeLoadOrder,
    failedTypes,
    removeFailedTypes
  } = useFetchResources();
  const { searchedResources } = useResourceFilters(
    resources,
    selectedResourceTypes,
    resourceSearch,
    typeLoadOrder,
    failedTypes
  );
  const {
    selectedResourceIds,
    setSelectedResourceIds,
    handleResourceToggle,
    handleSelectAllToggle,
    handleResetSelections,
    allSelectedResources,
    resourceTypeCounts
  } = useResourceSelection(resources, resourceType);

  // Log page mount for traceability
  React.useEffect(() => {
    logger.info('Page: SnapshotSetup mounted');
  }, []);

  //Pre-populate form from failed snapshot if navigating from Attention modal
  React.useEffect(() => {
    const snapshotData = (
      location.state as {
        snapshotData?: {
          name: string;
          sourceOrgName: string;
          resources: string[];
          snapshotCronExpression?: string;
          id: string;
          isPrePopulatedForm: boolean;
          exportAll?: boolean;
        };
      }
    )?.snapshotData;

    if (snapshotData) {
      //set form fields based on snapshot data passed through location state from Attention modal
      setSnapshotName(snapshotData.name);
      setSourceOrganization(snapshotData.sourceOrgName);
      setIsPrePopulatedForm(true);
      setPrePopulatedFormId(snapshotData.id);

      // Set schedule if this is a recurring snapshot
      if (snapshotData.snapshotCronExpression) {
        setIsRecurringEnabled(true);
        const schedule = getSchedule(snapshotData.snapshotCronExpression); // This will return null if parsing fails, but we can still set the schedule with what we have
        setSnapshotSchedule({
          minute: schedule?.minute ?? 0,
          hour: schedule?.hour ?? 0,
          days: schedule?.days ?? [],
          ampm: schedule?.ampm ?? 'AM'
        });
      }

      // Set full snapshot mode if this is a full snapshot
      if (snapshotData.exportAll) {
        setIsFullSnapshotMode(true);
        // For full snapshots, skip resource parsing since no specific resources are selected
        navigate(location.pathname, { replace: true });
        return;
      }

      // Parse resources from "type::id" format
      const resourcesByType: Record<string, string[]> = {};
      const resourceTypes = new Set<string>();

      snapshotData.resources.forEach((resource) => {
        const [apiType, id] = resource.split('::');
        if (!apiType || !id) return;

        // Convert API type to display name
        const displayType = mapApiTypeToDisplayName(apiType);

        resourceTypes.add(displayType); // Store display name
        if (!resourcesByType[displayType]) {
          resourcesByType[displayType] = [];
        }
        resourcesByType[displayType].push(id);
      });

      // Set selected resource types with display names
      const typesToFetch = Array.from(resourceTypes);
      setSelectedResourceTypes(typesToFetch); // This is now display names

      // Store for later matching
      sessionStorage.setItem('pendingResourceSelection', JSON.stringify(resourcesByType));

      // Fetch resources using display names
      if (typesToFetch.length > 0 && sourceOrganization) {
        fetchMultipleResourceTypes(typesToFetch, snapshotData.sourceOrgName);
        prevFetchedTypesRef.current = typesToFetch;
      }

      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate, setSnapshotName, sourceOrganization, fetchMultipleResourceTypes]);

  // Apply resource selection after resources are fetched
  React.useEffect(() => {
    const pendingSelection = sessionStorage.getItem('pendingResourceSelection');

    if (pendingSelection && resources.length > 0) {
      try {
        const resourcesByType: Record<string, string[]> = JSON.parse(pendingSelection);

        // Find and select matching resources
        const idsToSelect: string[] = [];
        resources.forEach((resource) => {
          const expectedIds = resourcesByType[resource.type];
          if (expectedIds) {
            const isMatch =
              expectedIds.includes(resource.id) ||
              expectedIds.includes('*') ||
              expectedIds.includes('**');
            if (isMatch) {
              idsToSelect.push(resource.id);
            }
          }
        });

        if (idsToSelect.length > 0) {
          setSelectedResourceIds(idsToSelect);
          sessionStorage.removeItem('pendingResourceSelection');
        }
      } catch (err) {
        logger.error('Failed to parse pending resource selection', { error: err });
        sessionStorage.removeItem('pendingResourceSelection');
      }
    }
  }, [resources, setSelectedResourceIds]);

  // Remove selected resource IDs when their types are unchecked
  // But preserve selections when new types are added
  const prevSelectedTypesRef = React.useRef<string[]>([]);
  React.useEffect(() => {
    const prevTypes = prevSelectedTypesRef.current;

    if (selectedResourceTypes.length === 0 && prevTypes.length > 0) {
      // If no types selected anymore, clear all selections
      setSelectedResourceIds([]);
      // Also clear pending selection since we're clearing everything
      sessionStorage.removeItem('pendingResourceSelection');
    } else if (prevTypes.length > selectedResourceTypes.length) {
      // Types were removed - filter out resource IDs belonging to removed types
      const removedTypes = prevTypes.filter((t) => !selectedResourceTypes.includes(t));
      if (removedTypes.length > 0) {
        // Use allSelectedResources to check types, not just current resources array
        setSelectedResourceIds((prevIds) => {
          return prevIds.filter((id) => {
            const resource = allSelectedResources.find((r) => r.id === id);
            return resource && !removedTypes.includes(resource.type);
          });
        });

        // ALSO update the pending selection to remove the removed types
        const pendingSelection = sessionStorage.getItem('pendingResourceSelection');
        if (pendingSelection) {
          try {
            const resourcesByType: Record<string, string[]> = JSON.parse(pendingSelection);
            // Remove the removed types from pending selection
            removedTypes.forEach((type) => {
              delete resourcesByType[type];
            });
            // Save back to sessionStorage or remove if empty
            if (Object.keys(resourcesByType).length > 0) {
              sessionStorage.setItem('pendingResourceSelection', JSON.stringify(resourcesByType));
            } else {
              sessionStorage.removeItem('pendingResourceSelection');
            }
          } catch {
            // If parsing fails, just remove it
            sessionStorage.removeItem('pendingResourceSelection');
          }
        }
      }
    }

    // When new types are added (length increases), selections are preserved automatically
    prevSelectedTypesRef.current = selectedResourceTypes;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResourceTypes]);
  // Fetch organizations on mount
  React.useEffect(() => {
    if (accessToken) {
      fetchOrganizations();
    }
  }, [accessToken, fetchOrganizations]);

  // Clear cache when source organization changes to get fresh data
  const prevSourceOrgRef = React.useRef<string>('');
  React.useEffect(() => {
    if (
      sourceOrganization &&
      prevSourceOrgRef.current &&
      prevSourceOrgRef.current !== sourceOrganization
    ) {
      logger.info('Source organization changed, clearing cache', {
        old: prevSourceOrgRef.current,
        new: sourceOrganization
      });
      clearOrgCache(prevSourceOrgRef.current);
    }
    prevSourceOrgRef.current = sourceOrganization;
  }, [sourceOrganization]);

  // Log resource type changes
  const prevResourceTypeRef = React.useRef<string>('');
  React.useEffect(() => {
    if (resourceType && prevResourceTypeRef.current && prevResourceTypeRef.current !== resourceType) {
      logger.info('Resource type changed in SnapshotSetup', {
        from: prevResourceTypeRef.current,
        to: resourceType,
        selectedCount: selectedResourceIds.length
      });
    }
    prevResourceTypeRef.current = resourceType;
  }, [resourceType, selectedResourceIds.length]);

  // When full snapshot mode is active, display all resource types in the table
  const displayedResources = React.useMemo(() => {
    if (isFullSnapshotMode) {
      return RESOURCE_TYPES.map((type) => ({
        id: `all-${type}`,
        name: '* (All)',
        type: type
      }));
    }
    return allSelectedResources;
  }, [isFullSnapshotMode, allSelectedResources]);

  // Track fetched types to determine if we have pending unfetched types
  const prevFetchedTypesRef = React.useRef<string[]>([]);

  // Determine if there are pending types that haven't been fetched
  const hasPendingTypes = React.useMemo(() => {
    if (selectedResourceTypes.length === 0) return false;
    const prevFetched = prevFetchedTypesRef.current;
    const newTypes = selectedResourceTypes.filter((t) => !prevFetched.includes(t));
    return newTypes.length > 0;
  }, [selectedResourceTypes]);

  // Manual fetch handler
  const handleFetchResources = React.useCallback(() => {
    if (!sourceOrganization || !accessToken) return;

    const prevFetched = prevFetchedTypesRef.current;
    const newTypes = selectedResourceTypes.filter((t) => !prevFetched.includes(t));

    if (newTypes.length > 0) {
      fetchMultipleResourceTypes(newTypes, sourceOrganization);
      prevFetchedTypesRef.current = selectedResourceTypes;
    }
  }, [sourceOrganization, selectedResourceTypes, accessToken, fetchMultipleResourceTypes]);

  // Clear resources when types are removed
  React.useEffect(() => {
    const prevFetched = prevFetchedTypesRef.current;

    if (selectedResourceTypes.length === 0 && prevFetched.length > 0) {
      clearResources();
      prevFetchedTypesRef.current = [];
    } else if (prevFetched.length > selectedResourceTypes.length) {
      // Types were removed - update the ref to only include currently selected types
      const removedTypes = prevFetched.filter((t) => !selectedResourceTypes.includes(t));
      prevFetchedTypesRef.current = prevFetched.filter((t) => selectedResourceTypes.includes(t));
      // Remove failed types for types that were deselected
      if (removedTypes.length > 0) {
        removeFailedTypes(removedTypes);
      }
    }
    // Note: We don't automatically fetch when types are added - user must click "Load Resources"
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedResourceTypes]);

  // Close dropdown when resourceType changes (so UI resets when switching)
  React.useEffect(() => setResourcesDropdownOpen(false), [resourceType]);

  // Close dropdown when clicking outside
  useOutsideClick([dropdownButtonRef, panelRef], () => setResourcesDropdownOpen(false));

  // Clear search when dropdown closes
  React.useEffect(() => {
    if (!resourcesDropdownOpen) setResourceSearch('');
  }, [resourcesDropdownOpen]);

  const openConfirmModal = React.useCallback((scope: 'all' | 'current') => {
    setConfirmScope(scope);
    setConfirmModalOpen(true);
  }, []);

  React.useEffect(() => {
    if (!isRecurringEnabled) {
      // Reset schedule when disabling recurring snapshots
      setSnapshotSchedule({
        days: [],
        hour: 7,
        minute: 0,
        ampm: 'AM'
      });
    }
  }, [isRecurringEnabled]);

  // Log snapshot schedule changes
  const prevScheduleRef = React.useRef<Schedule>(snapshotSchedule);
  React.useEffect(() => {
    if (isRecurringEnabled && JSON.stringify(prevScheduleRef.current) !== JSON.stringify(snapshotSchedule)) {
      logger.info('Snapshot schedule updated', {
        schedule: snapshotSchedule,
        cronExpression: snapshotCronExpression
      });
    }
    prevScheduleRef.current = snapshotSchedule;
  }, [snapshotSchedule, isRecurringEnabled, snapshotCronExpression]);

  const totalSelectedCount = displayedResources.length;

  const isFormValid = React.useMemo(() => {
    const basicValid = !!(
      snapshotName.trim() &&
      snapshotName.length > 1 &&
      sourceOrganization &&
      (totalSelectedCount > 0 || isFullSnapshotMode)
    );

    if (!basicValid) return false;

    // Check recurring snapshot validation
    if (isRecurringEnabled && snapshotSchedule.days.length === 0) {
      return false;
    }

    return true;
  }, [
    snapshotName,
    sourceOrganization,
    totalSelectedCount,
    isFullSnapshotMode,
    isRecurringEnabled,
    snapshotSchedule.days.length
  ]);

  // Button should be enabled only when form is fully valid
  const isButtonEnabled = isFormValid;
  const validateForm = useCallback(
    (name: string, org: string | null, count: number, fullMode: boolean): boolean => {
      const errors: string[] = [];
      if (!name.trim()) {
        errors.push('Please enter a Snapshot Name');
      }
      if (name.trim().length < 2) {
        errors.push('Snapshot Name must be at least 2 characters long');
      }
      if (!org) {
        errors.push('Please select a Source Organization');
      }
      if (count === 0 && !fullMode) {
        errors.push('Please select at least one resource to include in the snapshot');
      }
      if (isRecurringEnabled) {
        if (!snapshotSchedule.days || snapshotSchedule.days.length === 0) {
          errors.push('Please select at least one day or turn off the recurring snapshot toggle.');
        }
      }
      setValidationError(errors);
      if (errors.length > 0) {
        logger.warn('Snapshot form validation failed', {
          errors,
          formState: { name, org, resourceCount: count, fullMode, isRecurringEnabled }
        });
      }
      return errors.length === 0;
    },
    [isRecurringEnabled, snapshotSchedule.days]
  );

  React.useEffect(() => {
    if (validationError.length > 0 && isFormValid) {
      setValidationError([]);
    }
  }, [
    snapshotName,
    sourceOrganization,
    totalSelectedCount,
    isFullSnapshotMode,
    validationError,
    isFormValid
  ]);

  React.useEffect(() => {
    if (validationError.length > 0 && isFormValid) {
      setValidationError([]);
    }
  }, [validationError, isFormValid]);

  React.useEffect(() => {
    if (hasAttemptedSubmit) {
      validateForm(snapshotName, sourceOrganization, totalSelectedCount, isFullSnapshotMode);
    }
  }, [
    snapshotName,
    sourceOrganization,
    totalSelectedCount,
    isFullSnapshotMode,
    validateForm,
    hasAttemptedSubmit
  ]);

  const { filteredResourcesByType } = useResourceSearch(
    allSelectedResources,
    typeSearch,
    nameSearch
  );

  const filteredResourceTypes = React.useMemo(() => {
    if (!typeSearch.trim()) return RESOURCE_TYPES;
    const searchLower = typeSearch.trim().toLowerCase();
    return RESOURCE_TYPES.filter((type) => type.toLowerCase().includes(searchLower));
  }, [typeSearch]);

  // Use a fixed height so the scroller doesn't shrink when items are added.
  // `h-96` sets a consistent height; `overflow-y-auto` makes it scroll when content exceeds that height.
  const tableScrollClass = `h-72 overflow-y-auto p-3`;

  // Handle selecting all resource types with wildcard
  const handleSelectAllResourceTypes = React.useCallback(() => {
    if (!sourceOrganization) {
      setValidationError(['Please select a Source Organization first']);
      return;
    }

    // Clear any manual selections and enable full snapshot mode
    handleResetSelections('all');
    setIsFullSnapshotMode(true);

    logger.info('Enabled full snapshot mode - all resource types will be included', {
      count: RESOURCE_TYPES.length
    });
  }, [sourceOrganization, handleResetSelections, setValidationError]);

  // Disable full snapshot mode when user manually selects resources
  React.useEffect(() => {
    if (allSelectedResources.length > 0 && isFullSnapshotMode) {
      setIsFullSnapshotMode(false);
    }
  }, [allSelectedResources.length, isFullSnapshotMode]);

  // Calculate total counts for each resource type for display in the table header badges
  const totalCountForType = RESOURCE_TYPES.map((displayType) => {
    const totalCountForType = resources.filter((r) => r.type === displayType).length;
    return { displayType, totalCountForType };
  });

  const totalCountForAllTypes =
    totalCountForType.length > 0
      ? new Map(
        totalCountForType.map(({ displayType, totalCountForType }) => [
          displayType,
          totalCountForType
        ])
      )
      : resourceTypeCounts;

  const handleCaptureSnapshotConfirm = async () => {
    setConfirmLoading(true);
    setError(null);
    try {
      if (!accessToken || !userInfo?.orgId) {
        throw new Error('Missing authentication information');
      }

      // Find source org ID from selected org name
      const sourceOrg = findOrganization(sourceOrganization);

      if (!sourceOrg) {
        throw new Error('Could not find source organization ID');
      }

      // Group selected resources by type to detect if all resources of a type are selected
      const resourcesByType = new Map<string, typeof allSelectedResources>();
      allSelectedResources.forEach((resource: { id: string; name: string; type: string }) => {
        const existing = resourcesByType.get(resource.type) || [];
        resourcesByType.set(resource.type, [...existing, resource]);
      });

      let resourceStrings: string[] = [];

      if (isFullSnapshotMode) {
        // Full snapshot mode: send empty array to indicate no filtering (includes all current and future resources)
        resourceStrings = [];
      } else {
        // Build resources array: use "type::*" if all resources of that type are selected,
        // Manual selection mode: use existing logic
        // Group selected resources by type to detect if all resources of a type are selected
        const resourcesByType = new Map<string, typeof allSelectedResources>();
        allSelectedResources.forEach((resource) => {
          const existing = resourcesByType.get(resource.type) || [];
          resourcesByType.set(resource.type, [...existing, resource]);
        });

        // otherwise use individual "type::name" entries
        resourcesByType.forEach((selectedOfType, displayType) => {
          const apiType = mapResourceTypeToApiPath(displayType);

          // Check if all resources of this type are selected using tracked counts
          const isAllSelectedResources =
            totalCountForAllTypes !== undefined &&
            totalCountForAllTypes.get(displayType) !== undefined &&
            allSelectedResources.filter((r) => r.type === displayType).length ===
            totalCountForAllTypes.get(displayType);

          if (isAllSelectedResources) {
            // All resources of this type selected - use wildcard
            resourceStrings.push(`${apiType}::*`);
          } else {
            // Partial selection - add individual resources
            selectedOfType.forEach((resource: { id: string }) => {
              resourceStrings.push(`${apiType}::${resource.id}`);
            });
          }


        });
      }

      if (isPrePopulatedForm) {
        logger.info('Creating snapshot from pre-populated form', {
          snapshotName,
          sourceOrgId: sourceOrg.id,
          sourceOrgName: sourceOrg.name,
          resourceCount: resourceStrings.length,
          snapshotSchedule: snapshotSchedule,
          snapshotCronExpression: snapshotCronExpression
        });

        // Update the existing snapshot request (using same endpoint as migrations, but without target org)
        const response = await updateMigration(accessToken, prePopulatedFormId, {
          name: snapshotName,
          sourceOrgId: sourceOrg.id,
          sourceOrgName: getOrganizationKey(sourceOrg),
          resources: resourceStrings,
          ...(isRecurringEnabled &&
            snapshotCronExpression &&
            snapshotCronExpression.length > 0 && { snapshotCronExpression }),
          advanceToNextStatus: false,
          status: MigrationStatus.ExportQueued // Set status back to queued so it can be picked up by the scheduler again
        });

        logger.info('Snapshot updated successfully from pre-populated form', {
          snapshotId: response.id,
          status: response.status,
          snapshotSchedule: snapshotSchedule,
          snapshotCronExpression: snapshotCronExpression
        });
      } else {
        // Create the snapshot request (using same endpoint as migrations, but without target org)
        const response = await createMigration(accessToken, {
          name: snapshotName,
          customerId: userInfo.orgId,
          createdBy: userInfo.userEmail || userInfo.userName,
          sourceOrgId: sourceOrg.id,
          sourceOrgName: getOrganizationKey(sourceOrg),
          resources: resourceStrings,
          exportAll: isFullSnapshotMode,
          ...(isRecurringEnabled &&
            snapshotCronExpression &&
            snapshotCronExpression.length > 0 && { snapshotCronExpression })
        });

        logger.info('Snapshot created successfully', {
          snapshotId: response.id,
          status: response.status,
          snapshotSchedule: snapshotSchedule,
          snapshotCronExpression: snapshotCronExpression
        });
      }
      handleModalClose();

      // Navigate to dashboard
      navigate('/landing');
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to create snapshot';
      logger.error('Failed to create snapshot', { error: errorMsg });
      console.error('Snapshot creation failed', err);
      setError('An error occurred while creating the snapshot. Please try again.');
    } finally {
      setConfirmLoading(false);
    }
  };

  const leftContent = (
    <>
      <div>
        <div className="space-y-6">
          <div className={`flex flex-col sm:flex-row items-center ${ui.gap.md} mt-4`}>
            <label
              className={`w-48 ${ui.text.formSectionLabel} font-semibold text-gray-800 dark:text-gray-100`}
            >
              Snapshot Name:
            </label>
            <div className="flex-1 w-full">
              <input
                id="snapshotName"
                type="text"
                value={snapshotName}
                placeholder="Enter Snapshot Name"
                onChange={(e) => {
                  setSnapshotName(e.target.value);
                  if (hasAttemptedSubmit) {
                    validateForm(
                      e.target.value,
                      sourceOrganization,
                      totalSelectedCount,
                      isFullSnapshotMode
                    );
                  }
                }}
                className={`w-full ${ui.input.custom} border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 ${snapshotName.trim() && snapshotName.trim().length < 2
                  ? 'border-red-500 dark:border-red-600'
                  : 'border-gray-300 dark:border-gray-700'
                  }`}
              />
              {snapshotName.trim() && snapshotName.trim().length < 2 && (
                <p className={`mt-1 ${ui.text.xs} text-red-600 dark:text-red-400`}>
                  Snapshot Name must be at least 2 characters long
                </p>
              )}
            </div>
          </div>

          <div className={`flex flex-col sm:flex-row items-center ${ui.gap.md}`}>
            <OrganizationSelector
              orgVariant="sourceOrganization"
              value={sourceOrganization}
              onChange={(org) => {
                setSourceOrganization(org);
                if (hasAttemptedSubmit) {
                  validateForm(snapshotName, org, totalSelectedCount, isFullSnapshotMode);
                }
              }}
            />
          </div>

          <p
            className={`${ui.text.formSectionLabel} font-semibold text-gray-800 dark:text-gray-100 !mt-7`}
          >
            Created by:{' '}
            <span className="text-blue-700 dark:text-blue-500">{userInfo?.userName}</span>
          </p>

          <div>
            <div
              className={`flex lg:flex-col xl:flex-row items-center lg:items-start xl:items-center justify-between lg:gap-2 xl:gap-0 pt-6 border-t-2 border-gray-300 dark:border-gray-600 ${ui.mb.section}`}
            >
              <div className={`flex items-center ${ui.gap.sm}`}>
                <h3
                  className={`${ui.text.formStepTitle} font-extrabold text-gray-800 dark:text-gray-100`}
                >
                  Select Resources to Include
                </h3>
                <Tooltip
                  content={
                    <div className="space-y-3">
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                          Manual Selection
                        </p>
                        <p className="text-gray-700 dark:text-gray-300">
                          Select one or more resource types, then click "Load Resources" to fetch
                          resources. Then pick individual resources from the resources dropdown or
                          select all for each type. You can add or remove types and resources at any
                          time—your selections will be saved and seen in the table to the right.
                        </p>
                      </div>
                      <div className="border-t border-gray-200 dark:border-gray-600 pt-2">
                        <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                          Full Snapshot
                        </p>
                        <p className="text-gray-700 dark:text-gray-300">
                          Click "Select All Resources (Full Snapshot)" to automatically include all
                          resource types with wildcard selection (*) - no need to pick individual
                          types or resources.
                        </p>
                      </div>
                    </div>
                  }
                  position="right"
                  className={ui.text.formSectionLabel}
                />
              </div>
              <button
                type="button"
                onClick={
                  isFullSnapshotMode
                    ? () => setIsFullSnapshotMode(false)
                    : handleSelectAllResourceTypes
                }
                disabled={!sourceOrganization}
                className={`whitespace-nowrap shrink-0 lg:self-end xl:self-auto ${ui.button.md} rounded-lg font-semibold shadow transition ${sourceOrganization
                  ? isFullSnapshotMode
                    ? 'bg-white hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-600 cursor-pointer'
                    : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white cursor-pointer'
                  : 'bg-gray-400 dark:bg-gray-600 text-gray-200 cursor-not-allowed opacity-60'
                  }`}
              >
                {isFullSnapshotMode ? (
                  <>
                    <span className="lg:hidden">Exit Full Snapshot</span>
                    <span className="hidden lg:inline">Exit Full Snapshot</span>
                  </>
                ) : (
                  <>
                    <span className="lg:hidden">Select All Resources (Full Snapshot)</span>
                    <span className="hidden lg:inline">Full Snapshot</span>
                  </>
                )}
              </button>
            </div>

            <ResourceSelector
              resourceType={resourceType}
              onResourceTypeChange={setResourceType}
              selectedResourceTypes={selectedResourceTypes}
              onSelectedResourceTypesChange={setSelectedResourceTypes}
              resourceSearch={resourceSearch}
              onResourceSearchChange={setResourceSearch}
              resourcesDropdownOpen={resourcesDropdownOpen}
              setResourcesDropdownOpen={setResourcesDropdownOpen}
              uniqueResourceTypes={RESOURCE_TYPES}
              loadingResources={loadingResources}
              fetchProgress={fetchProgress}
              fetchError={resourceFetchError}
              organizationSelected={!!sourceOrganization}
              searchedResources={searchedResources}
              selectedResourceIds={selectedResourceIds}
              onToggleResource={handleResourceToggle}
              onSelectAll={handleSelectAllToggle}
              dropdownRef={dropdownRef}
              dropdownButtonRef={dropdownButtonRef}
              panelRef={panelRef}
              onFetchResources={handleFetchResources}
              hasPendingTypes={hasPendingTypes}
            />

            {isFullSnapshotMode && (
              <div
                className={`mt-4 ${ui.page.sectionPadding} bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg`}
              >
                <div className={`flex items-center ${ui.gap.sm}`}>
                  <svg
                    className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p
                    className={`${ui.text.label} font-semibold text-blue-800 dark:text-blue-300`}
                  >
                    Full Snapshot Mode Active
                  </p>
                </div>
                <p className={`${ui.text.xs} text-blue-700 dark:text-blue-400 mt-2`}>
                  All {RESOURCE_TYPES.length} resource types will be included with wildcard (*)
                  selection
                </p>
              </div>
            )}

            <div className={`flex items-center ${ui.gap.md} mt-6`}>
              <label className={`flex items-center ${ui.gap.sm} cursor-pointer`}>
                <input
                  type="checkbox"
                  checked={includeDependencies}
                  onChange={(e) => setIncludeDependencies(e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 rounded focus:ring-blue-500 focus:ring-2"
                />
                <span
                  className={`${ui.text.formSectionLabel} font-semibold text-gray-800 dark:text-gray-100`}
                >
                  Include Dependencies
                </span>
              </label>
              <Tooltip
                content={
                  <div className="space-y-3">
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        Checked (recommended)
                      </p>
                      <p className="text-gray-700 dark:text-gray-300">
                        Automatically discovers and exports all resources your selected resources
                        depend on (e.g. queues, skills, schedules, wrap-up codes). Dependencies
                        missing from an existing snapshot will be included on the next capture.
                      </p>
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-2">
                      <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                        Unchecked
                      </p>
                      <p className="text-gray-700 dark:text-gray-300">
                        Exports only the explicitly selected resources. Use this only if all
                        dependencies are already captured separately — missing dependencies will
                        make the snapshot incomplete.
                      </p>
                    </div>
                  </div>
                }
                position="right"
                className={ui.text.formSectionLabel}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Enable Recurring Snapshots */}
      <div className="pt-6 border-t-2 border-gray-300 dark:border-gray-600">
        <div
          className={`flex lg:flex-col xl:flex-row items-center lg:items-start xl:items-center justify-between lg:gap-2 xl:gap-0 ${ui.mb.title}`}
        >
          <div className={`flex items-center ${ui.gap.sm}`}>
            <h3
              className={`${ui.text.formStepTitle} font-extrabold text-gray-800 dark:text-gray-100`}
            >
              Make This a Recurring Snapshot
            </h3>
            <Tooltip
              content="(Optional) Set up a schedule for automatic recurring snapshots to ensure your data is regularly backed up without manual intervention."
              position="right"
              className={`${ui.text.formSectionLabel} font-semibold`}
            />
          </div>
          <button
            type="button"
            onClick={() => setIsRecurringEnabled(!isRecurringEnabled)}
            className={`lg:self-end xl:self-auto relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isRecurringEnabled ? 'bg-green-600 dark:bg-green-700' : 'bg-gray-400 dark:bg-gray-700'
              }`}
            role="switch"
            aria-checked={isRecurringEnabled}
            aria-label="Enable recurring snapshots"
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isRecurringEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
            />
          </button>
        </div>

        <AnimatePresence>
          {isRecurringEnabled && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: [0.4, 0, 0.2, 1]
              }}
              className="overflow-hidden"
            >
              <div className="space-y-5 mt-4">
                <div
                  className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg ${ui.page.sectionPadding}`}
                >
                  <div className={`flex items-center justify-between ${ui.mb.sm}`}>
                    <div className={`flex items-center ${ui.gap.sm} ${ui.mb.sm}`}>
                      <svg
                        className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <span
                        className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-200`}
                      >
                        Select day(s) to run snapshot
                      </span>
                    </div>
                    <span
                      className={`${ui.text.xs} font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full`}
                    >
                      {snapshotSchedule.days.length} day
                      {snapshotSchedule.days.length !== 1 ? 's' : ''} selected
                    </span>
                  </div>
                  <SchedulePicker
                    value={snapshotSchedule}
                    onChange={setSnapshotSchedule}
                    showTime={false}
                  />

                  <div className="border-t border-blue-200 dark:border-blue-700 my-4"></div>

                  {/* Time Picker */}
                  <div className={`flex items-center ${ui.gap.sm} mb-3`}>
                    <svg
                      className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span
                      className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-200`}
                    >
                      Select time to run snapshot
                    </span>
                    <span className={`${ui.text.xs} text-gray-400 dark:text-gray-500 font-normal ml-1`}>
                      ({getLocalTimezoneAbbr()})
                    </span>
                  </div>
                  <div
                    className={`flex items-center ${ui.gap.sm} bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-2 w-fit`}
                  >
                    <select
                      aria-label="Hour"
                      value={snapshotSchedule.hour ?? ''}
                      onChange={(e) =>
                        setSnapshotSchedule((s) => ({
                          ...s,
                          hour: e.target.value === '' ? null : Number(e.target.value)
                        }))
                      }
                      className={`w-14 h-8 text-center ${ui.text.label} rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="">HH</option>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                    <span className="text-lg text-gray-400 dark:text-gray-500 font-bold">:</span>
                    <select
                      aria-label="Minute"
                      value={snapshotSchedule.minute ?? ''}
                      onChange={(e) =>
                        setSnapshotSchedule((s) => ({
                          ...s,
                          minute: e.target.value === '' ? null : Number(e.target.value)
                        }))
                      }
                      className={`w-14 h-8 text-center ${ui.text.label} rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="">MM</option>
                      {Array.from({ length: 60 }, (_, i) => i).map((m) => (
                        <option key={m} value={m}>
                          {String(m).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                    <div className="flex rounded overflow-hidden ml-1">
                      <button
                        type="button"
                        onClick={() => setSnapshotSchedule((s) => ({ ...s, ampm: 'AM' }))}
                        className={`${ui.button.sm} h-8 font-semibold transition ${snapshotSchedule.ampm === 'AM'
                          ? 'bg-blue-600 dark:bg-blue-700 text-white'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                      >
                        AM
                      </button>
                      <button
                        type="button"
                        onClick={() => setSnapshotSchedule((s) => ({ ...s, ampm: 'PM' }))}
                        className={`${ui.button.sm} h-8 font-semibold transition ${snapshotSchedule.ampm === 'PM'
                          ? 'bg-blue-600 dark:bg-blue-700 text-white'
                          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                          }`}
                      >
                        PM
                      </button>
                    </div>
                  </div>
                  <p
                    className={`${ui.text.xs} text-gray-500 dark:text-gray-400 mt-2 flex items-center gap-1`}
                  >
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                    All times are shown in your local timezone
                  </p>
                </div>

                {/* Schedule Summary */}
                <div
                  className={`bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-lg ${ui.page.smPadding}`}
                >
                  <h4
                    className={`${ui.text.label} font-bold text-blue-900 dark:text-blue-200 mb-3 flex items-center ${ui.gap.xs}`}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Schedule Summary
                  </h4>
                  <div className="border-t border-blue-200 dark:border-blue-700 mb-1"></div>
                  <div className={`space-y-3 ${ui.text.label}`}>
                    <div>
                      <span className="text-gray-600 dark:text-gray-400 font-medium">
                        Full Schedule:
                      </span>
                      <p className="text-gray-900 dark:text-gray-100 font-semibold mt-1">
                        {snapshotScheduleDisplay ? (
                          snapshotScheduleDisplay
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500 italic">
                            None selected
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );

  const rightHeader = (
    <>
      <SelectedResourcesTableHeader
        typeSearch={typeSearch}
        onSetTypeSearchChange={setTypeSearch}
        nameSearch={nameSearch}
        onSetNameSearchChange={setNameSearch}
      />
    </>
  );

  const rightBody = (
    <>
      <SelectedResourcesTableBody
        filteredResourcesByType={filteredResourcesByType}
        filteredResourceTypes={filteredResourceTypes}
        typeSearch={typeSearch}
        nameSearch={nameSearch}
        isFullSnapshotMode={isFullSnapshotMode}
        resourceTypeCounts={totalCountForAllTypes}
        loadingResources={loadingResources && isPrePopulatedForm}
      />
    </>
  );

  const rightExtraContent = (
    <SelectedResourcesTableButtons
      totalSelectedCount={totalSelectedCount}
      selectedResourceIds={selectedResourceIds}
      openConfirmModal={openConfirmModal}
    />
  );

  const handleCaptureSnapshotButtonClick = () => {
    setHasAttemptedSubmit(true); // Set flag when button is clicked
    if (validateForm(snapshotName, sourceOrganization, totalSelectedCount, isFullSnapshotMode)) {
      handleCaptureSnapshotClick();
    }
  };

  const buttons = (
    <div className={`flex flex-col items-end ${ui.gap.sm}`}>
      {hasAttemptedSubmit && validationError && validationError.length > 0 && (
        <div className="px-4 py-2 bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg">
          <ul className="list-disc pl-5 space-y-1">
            {validationError.map((err, idx) => (
              <li key={idx} className={`${ui.text.label} text-red-700 dark:text-red-300`}>
                {err}
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex items-center space-x-3">
        <button
          className={`rounded-lg ${ui.button.md} text-white font-semibold shadow transition flex items-center ${ui.gap.sm}
            ${isButtonEnabled
              ? 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 cursor-pointer'
              : 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed opacity-60'
            }`}
          onClick={handleCaptureSnapshotButtonClick}
        >
          <svg
            className={`${ui.svg.lg}`}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          Capture Snapshot
        </button>
      </div>
    </div>
  );

  return (
    <>
      <SetupLayout
        title="Snapshot Details"
        leftContent={leftContent}
        rightHeader={rightHeader}
        rightBody={rightBody}
        rightContentWrapperClass={tableScrollClass}
        rightExtraContent={rightExtraContent}
        selectedResourceTitle={
          isFullSnapshotMode ? 'Full Snapshot (All Resources)' : selectedResourceTitle
        }
        selectedResourceCount={isFullSnapshotMode ? RESOURCE_TYPES.length : totalSelectedCount}
        buttonSection={buttons}
      />
      <Modal
        isOpen={modalState.variant === 'delete' ? confirmModalOpen : modalState.isOpen}
        variant={modalState.variant}
        title={
          modalState.variant === 'delete'
            ? confirmScope === 'all'
              ? 'Clear ALL Resource Type Selections'
              : 'Clear CURRENT Resource Type Selections'
            : 'Confirm Snapshot'
        }
        onClose={
          modalState.variant === 'delete'
            ? () => {
              logger.info('Modal closed', { type: 'delete', action: 'cancelled' });
              setConfirmModalOpen(false);
            }
            : () => {
              logger.info('Modal closed', { type: 'snapshot', action: 'cancelled' });
              handleModalClose();
            }
        }
        onConfirm={
          modalState.variant === 'delete'
            ? () => {
              if (confirmScope === 'all') {
                handleResetSelections('all');
                setIsFullSnapshotMode(false);
                setSelectedResourceTypes([]);
              } else {
                // Only clear the types the user just interacted with (the current selectedResourceTypes)
                handleResetSelections('current');
                setSelectedResourceTypes((prev) =>
                  prev.filter((type) => !selectedResourceTypes.includes(type))
                );
              }
              setConfirmModalOpen(false);
            }
            : handleCaptureSnapshotConfirm
        }
        confirmLabel={modalState.variant === 'delete' ? 'Yes, clear' : 'Run Snapshot'}
        cancelLabel="Cancel"
        showConfirmButton={true}
        showIcon={true}
        confirmLoading={confirmLoading}
        confirmLoadingLabel="Running snapshot..."
        showCloseButton
        setError={setError}
        error={error}
      >
        {
          ((): React.ReactNode => {
            if (modalState.variant === 'delete') {
              return (
                <>
                  <p className={`${ui.text.label} text-center text-gray-700 dark:text-gray-300`}>
                    {confirmScope === 'all'
                      ? 'This will remove all saved selections across every resource type. This action cannot be undone.'
                      : 'This will clear selections only for the currently selected resource type.'}
                  </p>
                </>
              );
            }

            if (modalState.variant === 'runSnapshot') {
              const resourcesByType = new Map<string, typeof allSelectedResources>();
              allSelectedResources.forEach(
                (resource: { type: string; id: string; name: string }) => {
                  const existing = resourcesByType.get(resource.type) || [];
                  resourcesByType.set(resource.type, [...existing, resource]);
                }
              );

              return (
                <div className="w-full max-w-2xl space-y-3">
                  {/* Summary Card */}
                  <div className="space-y-3">
                    {/* Name - Full Width */}
                    <div
                      className={`flex items-start ${ui.gap.md} ${ui.page.sectionPadding} bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700`}
                    >
                      <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                        <svg
                          className="w-4 h-4 text-purple-600 dark:text-purple-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`${ui.text.xs} font-medium text-gray-500 dark:text-gray-400 mb-1`}
                        >
                          Snapshot Name
                        </div>
                        <div
                          className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100 break-words`}
                        >
                          {snapshotName || '[Name]'}
                        </div>
                      </div>
                    </div>

                    {/* Organization - Full Width */}
                    <div
                      className={`flex items-start ${ui.gap.md} ${ui.page.sectionPadding} bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700`}
                    >
                      <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                        <svg
                          className="w-4 h-4 text-orange-600 dark:text-orange-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`${ui.text.xs} font-medium text-gray-500 dark:text-gray-400 mb-1`}
                        >
                          Organization
                        </div>
                        <div
                          className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100 break-words`}
                        >
                          {sourceOrganization ? (
                            <>
                              {sourceOrganization} - {(() => {
                                const org = findOrganization(sourceOrganization);
                                return formatRegion(org?.region);
                              })()}
                            </>
                          ) : '[Org]'}
                        </div>
                      </div>
                    </div>

                    {/* Resources - Full Width */}
                    <div
                      className={`flex items-start ${ui.gap.md} ${ui.page.sectionPadding} bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700`}
                    >
                      <div className="p-2 bg-blue-100 dark:bg-blue-800/50 rounded-lg">
                        <svg
                          className="w-4 h-4 text-blue-600 dark:text-blue-400"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`${ui.text.xs} font-medium text-gray-500 dark:text-gray-400 mb-1`}
                        >
                          {isFullSnapshotMode ? 'Resource Types' : 'Resources'}
                        </div>
                        <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                          {isFullSnapshotMode
                            ? RESOURCE_TYPES.length
                            : allSelectedResources.length}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Schedule (if exists) */}
                  {snapshotSchedule.days.length > 0 && (
                    <div
                      className={`${ui.mb.title} bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700 px-4 py-2.5`}
                    >
                      <div className={`flex items-center ${ui.gap.sm}`}>
                        <div className="p-2 bg-blue-100 dark:bg-blue-800/50 rounded-lg">
                          <svg
                            className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <div>
                          <div
                            className={`${ui.text.xs} font-medium text-blue-700 dark:text-blue-300 mb-1`}
                          >
                            Recurring Schedule
                          </div>
                          <div
                            className={`font-bold ${ui.text.label} text-blue-900 dark:text-blue-100`}
                          >
                            {snapshotScheduleDisplay ?? 'Schedule not set'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Resources Table */}
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <SelectedResourcesTableHeader
                      typeSearch={typeSearch}
                      onSetTypeSearchChange={setTypeSearch}
                      nameSearch={nameSearch}
                      onSetNameSearchChange={setNameSearch}
                    />
                    <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
                      <SelectedResourcesTableBody
                        filteredResourcesByType={filteredResourcesByType}
                        filteredResourceTypes={filteredResourceTypes}
                        typeSearch={typeSearch}
                        nameSearch={nameSearch}
                        isFullSnapshotMode={isFullSnapshotMode}
                        resourceTypeCounts={totalCountForAllTypes}
                      />
                    </div>
                  </div>
                </div>
              );
            }

            return null;
          })() as React.ReactNode
        }
      </Modal>
    </>
  );
};

export default CaptureSnapshot;
